import React, { useState, useRef, useLayoutEffect, FunctionComponent } from 'react';
import { makeStyles, createStyles, Theme, useTheme } from '@material-ui/core/styles';
import { Box, Typography, Dialog, DialogContent, Slider, useMediaQuery } from '@material-ui/core';
import clsx from 'clsx';
import Cropper from 'react-easy-crop';
import { Point, Area } from 'react-easy-crop/types';
import { Button } from 'components/UI';
import { croppImgAsBlob } from './cropImage';
import { resizeBlobToMaxBytes, TARGET_IMAGE_MB } from 'utils/ImageResizeUtils';

interface CropperImageProps {
    image: string;
    fileName: string;
    cropWidth: number;
    cropHeight: number;
    viewScale?: number;
    onCrop: (file: File) => void;
    onClose: () => void;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        dialog: {
            '& .MuiDialog-paper': {
                borderRadius: '10px',
                width: 'auto',
                maxWidth: 'calc(100vw - 64px)',
                alignItems: 'center'
            }
        },
        dialogMobile: {
            '& .MuiDialog-paper': {
                margin: 0,
                borderRadius: 0,
                maxHeight: '100vh',
                height: '100%',
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch'
            }
        },
        dialogContent: (props: CropperImageProps) => ({
            width: props.cropWidth * (props.viewScale || 1) + 80 + 'px',
            maxWidth: '100%',
            boxSizing: 'border-box',
            height: props.cropHeight * (props.viewScale || 1) + 194 + 'px',
            padding: '20px',
            flex: '0 0 auto',
            alignSelf: 'center'
        }),
        dialogContentMobile: {
            '&.MuiDialogContent-root': {
                width: '100%',
                maxWidth: '100%',
                flex: '1 1 auto',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
                boxSizing: 'border-box',
                padding: theme.spacing(2),
                paddingBottom: theme.spacing(1.5)
            }
        },
        cropperContainer: (props: CropperImageProps) => ({
            position: 'relative',
            width: props.cropWidth * (props.viewScale || 1) + 40 + 'px',
            height: props.cropHeight * (props.viewScale || 1) + 40 + 'px'
        }),
        cropperWrapperMobile: {
            position: 'relative' as const,
            flex: '1 1 auto',
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden',
            // Altura explícita: antes el flex no tenía alto y el área de recorte medía pocos px
            height: 'calc(100vh - 200px)',
            minHeight: 'min(55vh, 520px)',
            maxHeight: 'calc(100vh - 160px)'
        },
        footer: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100px',
            paddingTop: '30px',
            paddingLeft: '5px',
            paddingRight: '5px'
        },
        footerMobile: {
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            flexShrink: 0,
            paddingTop: '16px',
            paddingLeft: '4px',
            paddingRight: '4px',
            paddingBottom: '8px'
        },
        footerRow: {
            width: '100%'
        },
        zoom: {
            [theme.breakpoints.only('md')]: {
                fontSize: '10px',
                lineHeight: '16px'
            },
            [theme.breakpoints.only('lg')]: {
                fontSize: '12px',
                lineHeight: '18px'
            },
            [theme.breakpoints.up('xl')]: {
                fontSize: '18px',
                lineHeight: '24px'
            }
        },
        buttonsContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            marginTop: '10px',
            gap: '12px'
        }
    })
);

export const CropperImage: FunctionComponent<CropperImageProps> = props => {
    const classes = useStyles(props);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [croppedArea, setCroppedArea] = useState<Area | null>(null);
    const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);

    const baseCropW = props.cropWidth * (props.viewScale || 1);
    const baseCropH = props.cropHeight * (props.viewScale || 1);

    const cropperWrapperRef = useRef<HTMLDivElement>(null);
    const [containerSize, setContainerSize] = useState({ width: 280, height: 280 });

    useLayoutEffect(() => {
        if (!isMobile) return;

        const el = cropperWrapperRef.current;
        if (!el) return;

        const updateSize = () => {
            const { clientWidth, clientHeight } = el;
            if (clientWidth > 0 && clientHeight > 0) {
                setContainerSize({ width: clientWidth, height: clientHeight });
            }
        };

        updateSize();
        const observer = new ResizeObserver(updateSize);
        observer.observe(el);
        return () => observer.disconnect();
    }, [isMobile]);

    const cropCompleteHandler = (croppedAreaPercentage: Area, croppedAreaPixels: Area) => {
        setCroppedArea({
            x: croppedAreaPixels.x,
            y: croppedAreaPixels.y,
            width: croppedAreaPixels.width,
            height: croppedAreaPixels.height
        });
    };

    const cropHandler = async (image: string, croppedAreaPixels: Area) => {
        const croppedImage: Blob = await croppImgAsBlob(image, croppedAreaPixels);

        try {
            const resized = await resizeBlobToMaxBytes(croppedImage, props.fileName, {
                targetMb: TARGET_IMAGE_MB
            });
            props.onCrop(resized);
        } catch {
            const file = new File([croppedImage], props.fileName, { type: croppedImage.type });
            props.onCrop(file);
        }
    };

    const cropSizeDesktop = { width: baseCropW, height: baseCropH };

    const cropperStyle = isMobile
        ? {
              containerStyle: {
                  borderRadius: '10px',
                  border: '1px solid #ccc'
              },
              cropAreaStyle: { width: '100%', height: '100%' },
              mediaStyle: {}
          }
        : {
              containerStyle: { borderRadius: '10px', border: '1px solid black' },
              cropAreaStyle: { width: '100%', height: '100%' },
              mediaStyle: {}
          };

    const cropSize = isMobile ? containerSize : cropSizeDesktop;

    return (
        <Dialog
            open={true}
            onClose={props.onClose}
            className={clsx(classes.dialog, isMobile && classes.dialogMobile)}
            style={{ zIndex: 10002 }}
            fullScreen={isMobile}
            fullWidth={false}
            maxWidth={false}
        >
            <DialogContent className={isMobile ? classes.dialogContentMobile : classes.dialogContent}>
                {isMobile ? (
                    <div
                        className={classes.cropperWrapperMobile}
                        ref={cropperWrapperRef}
                    >
                        <Cropper
                            image={props.image}
                            crop={crop}
                            zoom={zoom}
                            aspect={props.cropWidth / props.cropHeight}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={cropCompleteHandler}
                            cropSize={cropSize}
                            minZoom={0.5}
                            maxZoom={5}
                            zoomSpeed={0.2}
                            style={cropperStyle}
                            restrictPosition={false}
                        />
                    </div>
                ) : (
                    <Box className={classes.cropperContainer}>
                        <Cropper
                            image={props.image}
                            crop={crop}
                            zoom={zoom}
                            aspect={props.cropWidth / props.cropHeight}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={cropCompleteHandler}
                            cropSize={cropSize}
                            minZoom={0.5}
                            maxZoom={5}
                            zoomSpeed={0.2}
                            style={cropperStyle}
                            restrictPosition={false}
                        />
                    </Box>
                )}
                <Box className={clsx(classes.footer, isMobile && classes.footerMobile)}>
                    <Box className={classes.footerRow}>
                        <Typography className={classes.zoom}>Zoom:</Typography>
                    </Box>
                    <Box className={classes.footerRow}>
                        <Slider
                            value={zoom}
                            min={0.5}
                            max={5}
                            step={0.1}
                            aria-labelledby="Zoom"
                            onChange={(event, zoom) => setZoom(typeof zoom === 'number' ? zoom : zoom[0])}
                            classes={{ root: 'slider' }}
                        />
                    </Box>
                    <Box className={classes.buttonsContainer}>
                        <Button
                            label="Crop"
                            onClick={() => cropHandler(props.image, croppedArea!)}
                            size="medium"
                        />
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default CropperImage;
